/**
 * FollowupFilters — controlled filter panel for the follow-ups list page
 * (Story 7.14b). Parent owns the `value`/`onChange` so it can serialize
 * into the URL/querystring; this component is purely presentational.
 *
 * Filters:
 *   - status multi-select (chip toggles — 5 statuses including 3 skipped_*)
 *   - campaign id (text input — UUID)
 *   - customer phone substring
 *   - from / to (datetime-local)
 */
import { type ChangeEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  ALL_FOLLOWUP_STATUSES,
  type FollowupExecutionStatus,
  type FollowupListFilters,
} from '@/lib/followups';

export interface FollowupFiltersProps {
  value: FollowupListFilters;
  onChange: (next: FollowupListFilters) => void;
  onClear: () => void;
}

export function FollowupFilters({ value, onChange, onClear }: FollowupFiltersProps) {
  const hasAnyFilter =
    value.statuses.length > 0 ||
    value.campaignId.length > 0 ||
    value.customerPhone.length > 0 ||
    value.from.length > 0 ||
    value.to.length > 0;

  function toggleStatus(s: FollowupExecutionStatus) {
    const next = value.statuses.includes(s)
      ? value.statuses.filter((x) => x !== s)
      : [...value.statuses, s];
    onChange({ ...value, statuses: next });
  }

  function setText(key: keyof FollowupListFilters) {
    return (e: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [key]: e.target.value });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Status filter">
        {ALL_FOLLOWUP_STATUSES.map((s) => {
          const active = value.statuses.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-background text-muted-foreground hover:bg-accent'
              }`}
              aria-pressed={active}
              data-testid={`followup-filter-status-${s}`}
            >
              {s}
            </button>
          );
        })}
        {hasAnyFilter ? (
          <Button size="sm" variant="ghost" onClick={onClear} data-testid="followup-filters-clear">
            Limpar
          </Button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="ff-campaign">Campanha (UUID)</Label>
          <Input
            id="ff-campaign"
            value={value.campaignId}
            onChange={setText('campaignId')}
            placeholder="opcional"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ff-phone">Telefone</Label>
          <Input
            id="ff-phone"
            value={value.customerPhone}
            onChange={setText('customerPhone')}
            placeholder="substring dos dígitos"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ff-from">Enviado de</Label>
          <Input id="ff-from" type="datetime-local" value={value.from} onChange={setText('from')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ff-to">Enviado até</Label>
          <Input id="ff-to" type="datetime-local" value={value.to} onChange={setText('to')} />
        </div>
      </div>
    </div>
  );
}
